// TrialExpiredPage.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Firebase
import { signOut } from 'firebase/auth';

// Services
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { getBaseKey, isBaseAccessible, loadAllBasesForUser } from 'services/bases/BaseReadService';

// CSS
import './TrialExpiredPage.css';

// Components
import Button from 'components/button/Button';
import LogoSmall from 'components/logo/small/LogoSmall';
import { auth } from 'firebaseConfig';
import { buildActiveBaseSettingPatch } from 'utilities/baseSettingsKeys';
import {
  BASE_LOCKED_TITLE,
  formatBaseLockedMemberCopy,
  formatBaseLockedOwnerCopy,
} from 'utilities/baseAccessCopy';
import { openBasePlanSettings } from 'utilities/baseLimitFeedback';

/**
 * TrialExpiredPage
 *
 * @returns {ReactNode} The rendered component.
 */
const TrialExpiredPage = ({ embedded = false }) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { user, userSettings, setUserSettings } = useUser();
  const { activeBase, setActiveBase } = useBase();

  // ----- STATE ----- //

  const [switchTargets, setSwitchTargets] = useState([]);
  const [isResolvingSwitchTargets, setIsResolvingSwitchTargets] = useState(false);

  const baseTitle = String(activeBase?.title || 'this base').trim() || 'this base';
  const activeBaseKey = getBaseKey(activeBase);
  const isOwner = String(activeBase?.ownerKey || '').trim() === String(user?.key || '').trim();

  // ----- MEMOS ----- //

  const ownerName = useMemo(() => {
    const ownerSummary = activeBase?.ownerSummary || activeBase?.owner || null;
    return String(
      ownerSummary?.name
      || ownerSummary?.title
      || ownerSummary?.displayName
      || 'the base owner'
    ).trim() || 'the base owner';
  }, [activeBase?.owner, activeBase?.ownerSummary]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    let isDisposed = false;

    if (!user?.key) {
      setSwitchTargets([]);
      return undefined;
    }

    setIsResolvingSwitchTargets(true);

    loadAllBasesForUser(user.key)
      .then(async (bases) => {
        if (isDisposed) return;

        const candidates = (Array.isArray(bases) ? bases : [])
          .filter((base) => {
            const baseKey = getBaseKey(base);
            return baseKey && baseKey !== activeBaseKey;
          });

        const accessible = [];
        for (const base of candidates) {
          if (await isBaseAccessible(base)) {
            accessible.push(base);
          }
        }

        setSwitchTargets(accessible);
      })
      .catch((error) => {
        if (isDisposed) return;
        console.warn('[TrialExpiredPage] failed to resolve switch targets:', error);
        setSwitchTargets([]);
      })
      .finally(() => {
        if (!isDisposed) {
          setIsResolvingSwitchTargets(false);
        }
      });

    return () => {
      isDisposed = true;
    };
  }, [activeBaseKey, user?.key]);

  // ----- HANDLERS ----- //

  /**
   * handleUpgradeClick
   *
   * Handles upgrade click.
   */
  const handleUpgradeClick = () => {
    openBasePlanSettings();
  };

  /**
   * handleSwitchBase
   *
   * Handles switch base.
   *
   * @param {unknown} base - base.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSwitchBase = async (base) => {
    if (!user?.key) return;

    const baseKey = getBaseKey(base);
    if (!baseKey) return;

    const normalizedBase = {
      ...(base || {}),
      key: baseKey,
    };

    setUserSettings && setUserSettings({
      ...(userSettings || {}),
      ...buildActiveBaseSettingPatch(baseKey),
      userKey: user.key,
    });
    setActiveBase && setActiveBase(normalizedBase);

    try {
      if (userSettings?.id || userSettings?.userKey) {
        await updateSetting(user.key, buildActiveBaseSettingPatch(baseKey));
      } else {
        await createSetting(user.key, buildActiveBaseSettingPatch(baseKey));
      }
    } catch (error) {
      if (error?.code === 'not-found') {
        await createSetting(user.key, buildActiveBaseSettingPatch(baseKey));
      } else {
        console.error('[TrialExpiredPage] update ACTIVE_BASE_KEY error:', error);
      }
    }
  };

  /**
   * handleSignOut
   *
   * Handles sign out.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSignOut = async () => {
    await signOut(auth);
  };

  // ----- RENDER ----- //

  return (
    <>
      <div
        className={`trial-expired-page-container${embedded ? ' embedded' : ''}`}
        style={{
          background: embedded ? 'transparent' : theme.background,
        }}
      >

        {/* CARD */}
        <div className="trial-expired-page-card">

          {/* LOGO */}
          {!embedded && (

            <div className="trial-expired-page-logo">
              <LogoSmall />
            </div>
          )}

          {/* TITLE */}
          <h1 className="trial-expired-page-title">
            {BASE_LOCKED_TITLE}
          </h1>

          {/* COPY */}
          {isOwner ? (

            <p className="trial-expired-page-copy">
              {formatBaseLockedOwnerCopy(baseTitle)}
            </p>
          ) : (

            <p className="trial-expired-page-copy">
              {formatBaseLockedMemberCopy({ ownerName, baseTitle })}
            </p>
          )}

          {/* ACTIONS */}
          <div className="trial-expired-page-actions">
            {isOwner && (
              <Button
                label="Choose a plan"
                onClick={handleUpgradeClick}
              />
            )}

            {switchTargets.map((base) => (
              <Button
                key={getBaseKey(base)}
                label={`Switch to ${String(base?.title || 'base').trim() || 'base'}`}
                addon="secondary"
                onClick={() => handleSwitchBase(base)}
              />
            ))}

            {/* COPY */}
            {!isResolvingSwitchTargets && switchTargets.length === 0 && !isOwner && (

              <p className="trial-expired-page-copy">
                Switch to another base from the space menu when one becomes available.
              </p>
            )}

            <Button
              label="Sign out"
              addon="secondary"
              onClick={handleSignOut}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Dev Tools
TrialExpiredPage.displayName = 'TrialExpiredPage';

export default TrialExpiredPage;
